import type { ReactNode } from 'react'

import { parseISO, differenceInSeconds } from 'date-fns'
import { styled } from 'styled-components'

import {
  getBuyingFormatSequence,
  getNumberOfUnitsInLot,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  type BidStatus,
  type OrchestratedAuctionListingDetails,
  contractByListingIdQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  InteractiveIcon,
  interpUri,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'
import type { OrderResponseDto } from '@b-stock/order-api-client'

import buyUrlPrefix from '@/buyer-portal/buyUrlPrefix'
import { BuyNowState } from '@/buyer-portal/components/features/listing/actions/auction/const'
import type { AuctionDetailState } from '@/buyer-portal/components/features/listing/actions/auction/useAuctionDetailState'
import { isContractListing } from '@/buyer-portal/components/features/listing/actions/auction/utils'
import { useUserAccountId } from '@/buyer-portal/helpers/accountId'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import useCompatibleRouter from '@/buyer-portal/helpers/useCompatibleRouter'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { redirectToAuth } from '@/helpers/auth'
import { FeatureFlags } from '@/helpers/featureFlags'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { orchestratedListingToGTMItemInput } from '@/helpers/telemetry/gtmItemAdapters'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { sendTypedGTMEvent } from '@/helpers/telemetry/sendTypedGTMEvent'
import type { GTMBuyNowIntentEvent } from '@/helpers/types/gtmTypes'

import { BuyNowStatus } from '../Tag'
import { ActionButton, OrangeActionButton, RedActionButton } from '../ui'

export const BuyNowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${designColors.neutral.black};
  ${Typography.Title3}
  margin-bottom: 0.5rem;
`
export const StyledInteractiveIcon = styled(InteractiveIcon)`
  font-size: 1rem;
  margin-left: 0.5rem;
  color: ${designColors.neutral.darkGray};

  @media ${Breakpoints.max.medium} {
    font-size: 0.75rem;
    margin-left: 1rem;
  }
`

export const Body = styled.div``

export const BuyNowDescription = styled.div`
  margin-top: 0.5rem;
  color: ${designColors.neutral.darkGray};
  ${Typography.Body3}
`

export const ns = 'Auction.details.buyNowActions'

const getButtonComponent = (variant: 'blue' | 'orange' | 'red') => {
  switch (variant) {
    case 'orange':
      return OrangeActionButton
    case 'red':
      return RedActionButton
    case 'blue':
    default:
      return ActionButton
  }
}

type ActionStatesReturnType = {
  showPrimaryButton: boolean
  primaryEnabled?: boolean
  primaryLabel?: ReactNode
  primaryButtonAppearance?: 'primary' | 'loading'
  handlePrimaryBtnOnClick?: () => void
  displayBuyNowDesc?: boolean
}

export const useActionStates = ({
  editDeliveryMethod,
  order,
  buyNowStatus,
  auctionDetailState,
  listing,
  quickBuy,
  shippingQuotePending,
}: {
  editDeliveryMethod: () => void
  order?: OrderResponseDto | null
  buyNowStatus?: BidStatus.NO_BID | BuyNowStatus
  auctionDetailState: AuctionDetailState
  listing: OrchestratedAuctionListingDetails
  quickBuy: () => void
  shippingQuotePending: boolean
}): ActionStatesReturnType => {
  const isLoggedIn = useIsLoggedIn()

  const router = useCompatibleRouter()

  const accountId = useUserAccountId(false)
  const { eligibleToBid, buyNowState, qualifiedToBid } = auctionDetailState

  const { trackButtonClicked } = useAnalytics()
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const isTlcDisplayed =
    !!listing.lot.retailValue || (getNumberOfUnitsInLot(listing.lot) ?? 0) > 0

  const { auction } = listing

  const { actualEndTime, numberOfBids } = auction

  const timeDiffInSeconds = differenceInSeconds(
    // oxlint-disable-next-line react/purity
    parseISO(actualEndTime || new Date().toDateString()),
    // oxlint-disable-next-line react/purity
    new Date()
  )

  const isContract = isContractListing(listing)

  const { data: executedContract } = contractByListingIdQuery.useWithOpts(
    [listing._id, accountId, 'buyer'],
    {
      enabled: buyNowStatus === BuyNowStatus.BOUGHT && isContract,
    }
  )

  const p2sData = useListingP2SData(listing.lot.locationOriginalId)

  if (
    buyNowState === BuyNowState.Scheduled ||
    (buyNowState === BuyNowState.Ended && buyNowStatus !== BuyNowStatus.BOUGHT)
  ) {
    return {
      showPrimaryButton: false,
    }
  }

  if (!isLoggedIn) {
    return {
      showPrimaryButton: true,
      primaryLabel: <FormattedMessage id="BuyNow.BuyNowActions.signIn" />,
      handlePrimaryBtnOnClick: () => {
        redirectToAuth()
      },
      displayBuyNowDesc: false,
    }
  }

  if (!eligibleToBid || buyNowStatus === BuyNowStatus.SOLD) {
    return {
      showPrimaryButton: false,
    }
  }

  if (!qualifiedToBid && !shippingQuotePending) {
    return {
      showPrimaryButton: true,
      primaryEnabled: true,
      primaryLabel: <FormattedMessage id={`BuyNow.BuyNowActions.buyNow`} />,
      handlePrimaryBtnOnClick: () => {
        editDeliveryMethod()
      },
    }
  }

  if (buyNowStatus === BuyNowStatus.BOUGHT) {
    return {
      showPrimaryButton: !(isContract && !executedContract?.groupId),
      primaryButtonAppearance: order || isContract ? 'primary' : 'loading',
      primaryLabel: (
        <FormattedMessage
          id={`BuyNow.BuyNowActions.${isContract ? 'checkContractDetails' : 'checkOrderDetails'}`}
        />
      ),
      handlePrimaryBtnOnClick: () => {
        if (!accountId) {
          // Shouldn't ever happen, but typescript can't guarantee this
          return
        }
        if (order) {
          router.push(
            `${buyUrlPrefix}${interpUri`/user/${accountId}/orders/details/${order._id}`}`
          )
        }
        if (isContract && executedContract?.groupId) {
          router.push(
            `${buyUrlPrefix}${interpUri`/user/${accountId}/contracts/details/${executedContract.groupId}`}`
          )
        }
      },
      displayBuyNowDesc: false,
    }
  } else {
    return {
      showPrimaryButton: true,
      primaryLabel: <FormattedMessage id="BuyNow.BuyNowActions.buyNow" />,
      displayBuyNowDesc: true,
      handlePrimaryBtnOnClick: () => {
        trackButtonClicked({
          buttonName: 'buy_now',
          time_left: timeDiffInSeconds,
          bids_existing: numberOfBids,
          is_new_bid_box_design: true,
          is_total_landed_cost_displayed: isTlcDisplayed,
          ...getListingAnalyticsData(listing, fmvBadgeFlag, p2sData),
        })

        const buyNowPrice = listing.auction.buyNow?.price || 0
        const itemInput = orchestratedListingToGTMItemInput(listing)
        const buyNowIntentEvent: GTMBuyNowIntentEvent = {
          event: 'buy_now_intent',
          platform: GTM_PLATFORM,
          item_id: listing._id,
          ecommerce: {
            currency: 'USD',
            value: buyNowPrice,
            items: [
              { ...itemInput, price: buyNowPrice, index: 0, quantity: 1 },
            ],
          },
        }
        sendTypedGTMEvent(buyNowIntentEvent)

        /**
         * Buy Now does not use Formik. Launch BuyNowConfirmationModal.
         */
        quickBuy()
      },
    }
  }
}

const BuyNowActions = ({
  editDeliveryMethod,
  quickBuy,
  buyNowStatus,
  order,
  auctionDetailState,
  listing,
  shippingQuotePending = false,
}: {
  editDeliveryMethod: () => void
  quickBuy: () => void
  buyNowStatus?: BidStatus.NO_BID | BuyNowStatus
  order?: OrderResponseDto | null
  auctionDetailState: AuctionDetailState
  listing: OrchestratedAuctionListingDetails
  shippingQuotePending?: boolean
}) => {
  const { auction } = listing

  const bidButtonColorVariant = useFlag(FeatureFlags.BID_BUTTON_COLOR)
  const SelectedActionButton = getButtonComponent(bidButtonColorVariant)

  const {
    primaryLabel,
    handlePrimaryBtnOnClick,
    displayBuyNowDesc,
    showPrimaryButton,
    primaryButtonAppearance,
  } = useActionStates({
    editDeliveryMethod,
    order,
    buyNowStatus,
    auctionDetailState,
    listing,
    quickBuy,
    shippingQuotePending,
  })

  if (!showPrimaryButton) return null

  const isLoading = primaryButtonAppearance === 'loading'
  const buyNowOnly = getBuyingFormatSequence(auction) === 'BINO'

  return (
    <Body>
      {auction.closed ? null : (
        <BuyNowHeader>
          <div>
            <FormattedMessage id={`${ns}.header`} />
            <StyledInteractiveIcon tooltipProps={{ placement: 'bottom' }}>
              <FormattedMessage
                id={`${ns}.tooltip.${buyNowOnly ? 'buyNowOnly' : 'buyNow'}`}
              />
            </StyledInteractiveIcon>
          </div>
        </BuyNowHeader>
      )}
      {/* oxlint-disable-next-line react/static-components */}
      <SelectedActionButton
        type="button"
        appearance={shippingQuotePending ? 'loading' : primaryButtonAppearance}
        disabled={shippingQuotePending || isLoading}
        onClick={handlePrimaryBtnOnClick}
      >
        {primaryLabel}
      </SelectedActionButton>
      {displayBuyNowDesc ? (
        <BuyNowDescription>
          <FormattedMessage
            id={`${ns}.${buyNowOnly ? 'descriptionBuyNowOnly' : 'description'}`}
          />
        </BuyNowDescription>
      ) : null}
    </Body>
  )
}

export default BuyNowActions
